import type { RankingEntry } from "../../services/dashboard-api";

const MEDALS = ["🥇", "🥈", "🥉"];
const MEDAL_LABELS = ["Gold medal, 1st place", "Silver medal, 2nd place", "Bronze medal, 3rd place"];

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}m ${rest}s`;
}

/** FR-035/FR-043: score ranking, already sorted (score desc, duration asc tie-break) by the API.
 * Renders the top 3 as a podium with medals — with only 1 or 2 finalized entries, shows only
 * that many places rather than an empty slot (spec.md edge case). Shared by the Manager's
 * dashboard, the CO Member's per-company view, and the employee's ranking-only page (T095). */
export function Ranking({ entries }: { entries: RankingEntry[] }) {
  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  if (entries.length === 0) {
    return <p className="text-sm text-body">No finalized results yet.</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-end justify-center gap-4">
        {podium.map((entry, i) => (
          <div
            key={entry.memberId}
            className="flex flex-col items-center gap-1 rounded bg-surface-alt px-4 py-3"
            style={{ order: i === 0 ? 2 : i === 1 ? 1 : 3 }}
          >
            <span className="text-2xl" role="img" aria-label={MEDAL_LABELS[i]}>
              {MEDALS[i]}
            </span>
            <span className="font-sans text-sm text-ink">{entry.fullName}</span>
            <span className="text-sm text-body">
              {entry.scorePercent}% · {formatDuration(entry.durationSeconds)}
            </span>
          </div>
        ))}
      </div>

      {rest.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-surface-alt text-body">
              <th className="py-2">Rank</th>
              <th>Name</th>
              <th>Score</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {rest.map((entry, i) => (
              <tr key={entry.memberId} className="border-b border-surface-alt">
                <td className="py-2 text-ink">{i + 4}</td>
                <td>{entry.fullName}</td>
                <td>{entry.scorePercent}%</td>
                <td>{formatDuration(entry.durationSeconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
